// Ship 2 — Firm Dashboard tab.  Bar-audit / partner-comp analytics view showing
// utilization / realization / collection metrics for a given year, with an
// optional prior-year overlay.
//
// Backend contract (POST /api/dashboard/firm):
//   {
//     utilization: {status:'ok'|'forbidden'|'unavailable', monthly:[...], totals:{...}, meta:{...}},
//     realization: (same shape),
//     collection:  (same shape),
//     prior_year:  {available: bool, hidden_reason: 'less_than_3_months'|null}
//   }
//
// Per-section rendering:
//   - status='ok'          → render chart + totals
//   - status='forbidden'   → hidden entirely (no visible-empty leak per CRIT-3)
//   - status='unavailable' → friendly "no data" message
//
// Prior-year Line is fetched via a second POST (year=prev) and overlayed
// as a dashed line.  Hidden when prior_year.hidden_reason === 'less_than_3_months'.

import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Legend, CartesianGrid,
} from 'recharts'
import api from '../services/api'
import { SkeletonCard, LoadingRegion } from './Skeleton'
import EmptyState from './EmptyState'
import { useChartSizing } from '../hooks/useDisplayZoom'

const CURRENT_YEAR = new Date().getFullYear()

// Utilization bars: emerald=billable, amber=non-billable, gray=untracked
const UTIL_COLORS = {
  billable:     '#10b981',
  non_billable: '#f59e0b',
  untracked:    '#9ca3af',
}
const REAL_COLOR = '#7c3aed' // purple
const COLLECT_COLOR = '#2563eb' // blue
const PRIOR_COLOR = '#94a3b8' // slate — muted for overlay

function fmtDollars(v) {
  if (v == null) return '—'
  const n = Number(v) || 0
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}
function fmtHours(v) {
  if (v == null) return '—'
  const n = Number(v) || 0
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hr`
}
function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  return `${n.toFixed(1)}%`
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Merge current + prior monthly arrays into one array keyed by month index.
// Prior year values become `<field>_prior` so recharts can stack them as a
// separate Line series.
function mergeSeries(current, prior, valueKey) {
  const byIdx = {}
  for (let i = 0; i < 12; i++) {
    byIdx[i] = { month: MONTH_NAMES[i] }
  }
  ;(current || []).forEach((row) => {
    const [, m] = (row.month || '').split('-')
    const idx = parseInt(m, 10) - 1
    if (idx >= 0 && idx < 12) {
      byIdx[idx] = { ...byIdx[idx], ...row, month: MONTH_NAMES[idx] }
    }
  })
  ;(prior || []).forEach((row) => {
    const [, m] = (row.month || '').split('-')
    const idx = parseInt(m, 10) - 1
    if (idx >= 0 && idx < 12) {
      byIdx[idx][`${valueKey}_prior`] = row[valueKey]
    }
  })
  return Object.values(byIdx)
}

// Small dropdown+arrows year picker (like the spec: typeahead + prev/next).
function YearPicker({ year, setYear }) {
  const min = 2000
  const max = CURRENT_YEAR + 1
  const canPrev = year > min
  const canNext = year < max
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => canPrev && setYear(year - 1)}
        disabled={!canPrev}
        title="Previous year"
        className="w-8 h-8 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        ‹
      </button>
      <input
        type="number"
        min={min}
        max={max}
        value={year}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10)
          if (Number.isFinite(v) && v >= min && v <= max) setYear(v)
        }}
        className="w-24 text-center border border-gray-300 rounded px-2 py-1.5 text-sm"
      />
      <button
        type="button"
        onClick={() => canNext && setYear(year + 1)}
        disabled={!canNext}
        title="Next year"
        className="w-8 h-8 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        ›
      </button>
    </div>
  )
}

// Per-section units toggle.  Utilization has 3 (hr / $ / %), the financial
// sections have (dollars / %) since "hours" isn't meaningful there.
function UnitsToggle({ available, value, onChange }) {
  const LABELS = { hr: 'Hr', dollars: '$', pct: '%' }
  return (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs">
      {available.map((u) => (
        <button
          key={u}
          type="button"
          onClick={() => onChange(u)}
          className={`px-3 py-1.5 font-medium ${
            value === u
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {LABELS[u]}
        </button>
      ))}
    </div>
  )
}

// ── Section: Utilization ─────────────────────────────────────
function UtilizationSection({ current, prior, units, priorMeta }) {
  const chart = useChartSizing() // Recharts px follow the automatic zoom
  const showPriorLine = prior && prior.status === 'ok' && !priorMeta?.hidden_reason
  const priorKey = units === 'hr' ? 'logged_hours'
    : units === 'dollars' ? 'billed_amount'
    : 'utilization_pct'

  // Build stacked bar data.  Monthly rows carry billable_hours/non_billable_hours/
  // capacity_hours (or the $-amount equivalents when units='dollars').  Untracked
  // is computed as max(0, capacity - logged).
  const monthly = (current.monthly || []).map((row) => {
    // Row shape per backend: { month, capacity_hours, logged_hours,
    //   billable_hours, non_billable_hours, no_charge_hours,
    //   billable_amount, non_billable_amount, ... } (depends on unit)
    const capacity   = Number(row.capacity_hours || row.capacity || 0)
    const billable   = Number(row.billable_hours || row.billable || 0)
    const nonBill    = Number(row.non_billable_hours || row.non_billable || 0)
    const untracked  = Math.max(0, capacity - billable - nonBill)
    if (units === 'dollars') {
      return {
        month: row.month,
        billable: Number(row.billable_amount || row.billable || 0),
        non_billable: Number(row.non_billable_amount || row.non_billable || 0),
        untracked: 0, // untracked has no $ value
      }
    }
    if (units === 'pct') {
      return {
        month: row.month,
        billable: capacity > 0 ? +(100 * billable / capacity).toFixed(1) : 0,
        non_billable: capacity > 0 ? +(100 * nonBill / capacity).toFixed(1) : 0,
        untracked: capacity > 0 ? +(100 * untracked / capacity).toFixed(1) : 0,
      }
    }
    // hr (default)
    return { month: row.month, billable, non_billable: nonBill, untracked }
  })

  const chartData = mergeSeries(monthly, showPriorLine ? (prior.monthly || []) : null, priorKey)

  const totals = current.totals || {}
  const fmt = units === 'hr' ? fmtHours : units === 'dollars' ? fmtDollars : fmtPct

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">📊 Utilization</h3>
          <p className="text-xs text-gray-500">Billable + non-billable + untracked hours vs capacity</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatTile label="Billable" value={fmt(totals.billable_hours ?? totals.billable_amount ?? totals.billable_pct)} color="text-emerald-700 bg-emerald-50" />
        <StatTile label="Non-Billable" value={fmt(totals.non_billable_hours ?? totals.non_billable_amount ?? totals.non_billable_pct)} color="text-amber-700 bg-amber-50" />
        <StatTile label="Capacity" value={fmtHours(totals.capacity_hours)} color="text-gray-700 bg-gray-50" />
        <StatTile label="Utilization" value={fmtPct(totals.utilization_pct)} color="text-primary-700 bg-primary-50" />
      </div>
      <ResponsiveContainer width="100%" height={chart.height(280)}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="month" fontSize={chart.font} height={chart.xAxisHeight} tickLine={false} axisLine={false} />
          <YAxis fontSize={chart.font} width={chart.yAxisWidth} tickLine={false} axisLine={false} />
          <Tooltip formatter={(v) => (typeof v === 'number' ? (units === 'dollars' ? fmtDollars(v) : units === 'pct' ? fmtPct(v) : fmtHours(v)) : v)} />
          <Legend wrapperStyle={{ fontSize: chart.font }} />
          <Bar dataKey="billable" stackId="u" fill={UTIL_COLORS.billable} name="Billable" />
          <Bar dataKey="non_billable" stackId="u" fill={UTIL_COLORS.non_billable} name="Non-Billable" />
          <Bar dataKey="untracked" stackId="u" fill={UTIL_COLORS.untracked} name="Untracked" radius={[4, 4, 0, 0]} />
          {showPriorLine && (
            <Line
              type="monotone"
              dataKey={`${priorKey}_prior`}
              stroke={PRIOR_COLOR}
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
              name="Prior year"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {prior && prior.status === 'ok' && priorMeta?.hidden_reason === 'less_than_3_months' && (
        <p className="text-xs italic text-gray-500 mt-2">
          Prior year: less than 3 months of data, overlay hidden.
        </p>
      )}
    </div>
  )
}

// ── Section: Realization ─────────────────────────────────────
function RealizationSection({ current, prior, units, priorMeta }) {
  const chart = useChartSizing() // Recharts px follow the automatic zoom
  const showPriorLine = prior && prior.status === 'ok' && !priorMeta?.hidden_reason
  const primaryKey = units === 'pct' ? 'realization_pct' : 'billed_fees'
  const monthly = (current.monthly || []).map((row) => ({
    month: row.month,
    billed_fees: Number(row.billed_fees || 0),
    standard_value: Number(row.standard_value || 0),
    realization_pct: row.realization_pct == null ? 0 : Number(row.realization_pct),
  }))
  const chartData = mergeSeries(monthly, showPriorLine ? (prior.monthly || []) : null, primaryKey)
  const totals = current.totals || {}
  const fmt = units === 'dollars' ? fmtDollars : fmtPct
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">💵 Realization</h3>
          <p className="text-xs text-gray-500">Billed fees vs standard-rate value of work</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <StatTile label="Billed fees" value={fmtDollars(totals.billed_fees)} color="text-purple-700 bg-purple-50" />
        <StatTile label="Standard value" value={fmtDollars(totals.standard_value)} color="text-gray-700 bg-gray-50" />
        <StatTile label="Realization" value={fmtPct(totals.realization_pct)} color="text-primary-700 bg-primary-50" />
      </div>
      <ResponsiveContainer width="100%" height={chart.height(280)}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="month" fontSize={chart.font} height={chart.xAxisHeight} tickLine={false} axisLine={false} />
          <YAxis fontSize={chart.font} width={chart.yAxisWidth} tickLine={false} axisLine={false} tickFormatter={(v) => units === 'dollars' ? `$${Math.round(v / 1000)}k` : `${v}%`} />
          <Tooltip formatter={(v) => (typeof v === 'number' ? fmt(v) : v)} />
          <Legend wrapperStyle={{ fontSize: chart.font }} />
          <Bar dataKey={primaryKey} fill={REAL_COLOR} name={units === 'pct' ? 'Realization %' : 'Billed fees'} radius={[4, 4, 0, 0]} />
          {showPriorLine && (
            <Line
              type="monotone"
              dataKey={`${primaryKey}_prior`}
              stroke={PRIOR_COLOR}
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
              name="Prior year"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {prior && prior.status === 'ok' && priorMeta?.hidden_reason === 'less_than_3_months' && (
        <p className="text-xs italic text-gray-500 mt-2">
          Prior year: less than 3 months of data, overlay hidden.
        </p>
      )}
    </div>
  )
}

// ── Section: Collection ──────────────────────────────────────
function CollectionSection({ current, prior, units, priorMeta }) {
  const chart = useChartSizing() // Recharts px follow the automatic zoom
  const showPriorLine = prior && prior.status === 'ok' && !priorMeta?.hidden_reason
  const primaryKey = units === 'pct' ? 'collection_pct' : 'collected'
  const monthly = (current.monthly || []).map((row) => ({
    month: row.month,
    collected: Number(row.collected || 0),
    invoiced: Number(row.invoiced || 0),
    collection_pct: row.collection_pct == null ? 0 : Number(row.collection_pct),
  }))
  const chartData = mergeSeries(monthly, showPriorLine ? (prior.monthly || []) : null, primaryKey)
  const totals = current.totals || {}
  const fmt = units === 'dollars' ? fmtDollars : fmtPct
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">🏦 Collection</h3>
          <p className="text-xs text-gray-500">Cash collected in period vs invoiced in period</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <StatTile label="Collected" value={fmtDollars(totals.collected)} color="text-blue-700 bg-blue-50" />
        <StatTile label="Invoiced" value={fmtDollars(totals.invoiced)} color="text-gray-700 bg-gray-50" />
        <StatTile label="Collection" value={fmtPct(totals.collection_pct)} color="text-primary-700 bg-primary-50" />
      </div>
      <ResponsiveContainer width="100%" height={chart.height(280)}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="month" fontSize={chart.font} height={chart.xAxisHeight} tickLine={false} axisLine={false} />
          <YAxis fontSize={chart.font} width={chart.yAxisWidth} tickLine={false} axisLine={false} tickFormatter={(v) => units === 'dollars' ? `$${Math.round(v / 1000)}k` : `${v}%`} />
          <Tooltip formatter={(v) => (typeof v === 'number' ? fmt(v) : v)} />
          <Legend wrapperStyle={{ fontSize: chart.font }} />
          <Bar dataKey={primaryKey} fill={COLLECT_COLOR} name={units === 'pct' ? 'Collection %' : 'Collected'} radius={[4, 4, 0, 0]} />
          {showPriorLine && (
            <Line
              type="monotone"
              dataKey={`${primaryKey}_prior`}
              stroke={PRIOR_COLOR}
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
              name="Prior year"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {prior && prior.status === 'ok' && priorMeta?.hidden_reason === 'less_than_3_months' && (
        <p className="text-xs italic text-gray-500 mt-2">
          Prior year: less than 3 months of data, overlay hidden.
        </p>
      )}
    </div>
  )
}

function StatTile({ label, value, color }) {
  return (
    <div className={`rounded-lg p-3 ${color || 'bg-gray-50'}`}>
      <p className="text-xs font-medium uppercase opacity-80">{label}</p>
      <p className="text-lg font-bold mt-1">{value}</p>
    </div>
  )
}

function UnavailableCard({ title, reason }) {
  const msg = reason === 'no_data'
    ? 'No data for this period yet.'
    : reason === 'partial_year'
    ? 'Partial-year data — check back later.'
    : 'No data available.'
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 italic">{msg}</p>
    </div>
  )
}

export default function FirmDashboardTab({ user }) {
  const [year, setYear] = useState(() => {
    try {
      const stored = parseInt(localStorage.getItem('lextrack_firm_dashboard_year'), 10)
      return Number.isFinite(stored) && stored >= 2000 && stored <= CURRENT_YEAR + 1 ? stored : CURRENT_YEAR
    } catch { return CURRENT_YEAR }
  })
  useEffect(() => {
    try { localStorage.setItem('lextrack_firm_dashboard_year', String(year)) } catch {}
  }, [year])

  // Per-section units toggle (persisted).
  const [utilUnits, setUtilUnits] = useState(() => {
    try { return localStorage.getItem('lextrack_firm_dashboard_util_units') || 'hr' } catch { return 'hr' }
  })
  const [realUnits, setRealUnits] = useState(() => {
    try { return localStorage.getItem('lextrack_firm_dashboard_real_units') || 'dollars' } catch { return 'dollars' }
  })
  const [collectUnits, setCollectUnits] = useState(() => {
    try { return localStorage.getItem('lextrack_firm_dashboard_collect_units') || 'dollars' } catch { return 'dollars' }
  })
  useEffect(() => { try { localStorage.setItem('lextrack_firm_dashboard_util_units', utilUnits) } catch {} }, [utilUnits])
  useEffect(() => { try { localStorage.setItem('lextrack_firm_dashboard_real_units', realUnits) } catch {} }, [realUnits])
  useEffect(() => { try { localStorage.setItem('lextrack_firm_dashboard_collect_units', collectUnits) } catch {} }, [collectUnits])

  const [data, setData] = useState(null)
  const [priorData, setPriorData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchAll = useCallback(async () => {
    setLoading(true); setError('')
    try {
      // Current-year fetch always uses section='all' with the union of currently
      // selected units.  Backend defaults units per section when passed a single
      // string; the endpoint accepts a section-specific request, but for a
      // combined dashboard we just fire once and let each section render with
      // its own units toggle.  Backend response carries `units` per section so
      // if we ever want per-section fetches we can extend.
      const [curRes, priorRes] = await Promise.all([
        api.post('/api/dashboard/firm', { year, section: 'all' }),
        // Prior-year overlay: separate call per M2 spec.  Cached separately.
        // Silently no-op if prior-year not available (backend will still send
        // the constant envelope though).
        api.post('/api/dashboard/firm', { year: year - 1, section: 'all' }).catch(() => null),
      ])
      setData(curRes.data)
      setPriorData(priorRes?.data || null)
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load firm dashboard')
      setData(null)
      setPriorData(null)
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { fetchAll() }, [fetchAll])

  const priorMeta = data?.prior_year || null

  const sections = useMemo(() => {
    if (!data) return null
    return {
      utilization: data.utilization || { status: 'unavailable', reason: 'no_data' },
      realization: data.realization || { status: 'unavailable', reason: 'no_data' },
      collection:  data.collection  || { status: 'unavailable', reason: 'no_data' },
    }
  }, [data])

  const priorSections = useMemo(() => {
    if (!priorData) return {}
    return {
      utilization: priorData.utilization,
      realization: priorData.realization,
      collection:  priorData.collection,
    }
  }, [priorData])

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">📊 Firm Dashboard</h3>
          <p className="text-xs text-gray-500">Bar-audit / partner-comp analytics for {year}</p>
        </div>
        <div className="flex items-center gap-3">
          <YearPicker year={year} setYear={setYear} />
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-wait"
          >
            {loading ? '⟳ Refreshing…' : '⟳ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-white rounded-2xl shadow-sm border border-red-200 mb-4">
          <EmptyState
            variant="error"
            icon="⚠️"
            title="Could not load firm dashboard"
            description={error}
            action={{ label: 'Try again', onClick: fetchAll }}
          />
        </div>
      )}

      {loading && !data && (
        <LoadingRegion label="Loading firm dashboard">
          <div className="grid grid-cols-1 gap-4">
            <SkeletonCard height={360} />
            <SkeletonCard height={360} />
            <SkeletonCard height={360} />
          </div>
        </LoadingRegion>
      )}

      {sections && (
        <div className="space-y-4">
          {/* Utilization section */}
          {sections.utilization.status === 'ok' && (
            <div>
              <div className="flex items-center justify-end mb-2">
                <UnitsToggle available={['hr', 'dollars', 'pct']} value={utilUnits} onChange={setUtilUnits} />
              </div>
              <UtilizationSection
                current={sections.utilization}
                prior={priorSections.utilization}
                units={utilUnits}
                priorMeta={priorMeta}
              />
            </div>
          )}
          {sections.utilization.status === 'unavailable' && (
            <UnavailableCard title="📊 Utilization" reason={sections.utilization.reason} />
          )}
          {/* status === 'forbidden' → hidden entirely per CRIT-3 */}

          {/* Realization section */}
          {sections.realization.status === 'ok' && (
            <div>
              <div className="flex items-center justify-end mb-2">
                <UnitsToggle available={['dollars', 'pct']} value={realUnits} onChange={setRealUnits} />
              </div>
              <RealizationSection
                current={sections.realization}
                prior={priorSections.realization}
                units={realUnits}
                priorMeta={priorMeta}
              />
            </div>
          )}
          {sections.realization.status === 'unavailable' && (
            <UnavailableCard title="💵 Realization" reason={sections.realization.reason} />
          )}

          {/* Collection section */}
          {sections.collection.status === 'ok' && (
            <div>
              <div className="flex items-center justify-end mb-2">
                <UnitsToggle available={['dollars', 'pct']} value={collectUnits} onChange={setCollectUnits} />
              </div>
              <CollectionSection
                current={sections.collection}
                prior={priorSections.collection}
                units={collectUnits}
                priorMeta={priorMeta}
              />
            </div>
          )}
          {sections.collection.status === 'unavailable' && (
            <UnavailableCard title="🏦 Collection" reason={sections.collection.reason} />
          )}

          {/* If EVERY visible section is forbidden, show a friendly empty state
              so the tab isn't blank — mirrors the "you don't have any metrics"
              signal without leaking which specific perms are missing. */}
          {sections.utilization.status === 'forbidden'
            && sections.realization.status === 'forbidden'
            && sections.collection.status === 'forbidden' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
                <EmptyState
                  icon="🔒"
                  title="No firm-wide metrics available"
                  description="Your permissions don't include firm-wide time or financial visibility. Contact your administrator if you need access."
                />
              </div>
            )}
        </div>
      )}
    </div>
  )
}
